import { extractErrorMessage } from '../utils/errorHandling';
import type { InvestigationLensError, InvestigationLensErrorCode } from './types';

/**
 * Request passed to the lens implementation registered by the Grafana
 * Assistant plugin.
 */
export interface InvestigationLensRequest {
  investigationId: string;
  /** JSON Schema describing the shape of the desired structured view. */
  schema: unknown;
  /** Attribution for the generation, e.g. `'grafana-irm/incident-detail'`. */
  origin: string;
  /** When true, bypass the cached result and regenerate. */
  refresh?: boolean;
}

/**
 * The lens implementation provided by the Grafana Assistant plugin. Resolves
 * with data conforming to the request's schema, or rejects with an error
 * carrying an {@link InvestigationLensErrorCode} `code` property.
 */
export type InvestigationLensImplementation = (request: InvestigationLensRequest) => Promise<unknown>;
export type InvestigationLensInitializer = () => Promise<void>;

const LENS_ERROR_CODES: readonly InvestigationLensErrorCode[] = [
  'unavailable',
  'no_report',
  'permission_denied',
  'validation_failed',
  'llm_error',
];

function isLensErrorCode(value: unknown): value is InvestigationLensErrorCode {
  return typeof value === 'string' && (LENS_ERROR_CODES as readonly string[]).includes(value);
}

/**
 * Typed error the lens implementation throws to signal a specific failure
 * mode. Consumers should not need this class — the hook converts failures to
 * plain {@link InvestigationLensError} values. Conversion is structural (it
 * reads the `code` property), so it works even when the host plugin and the
 * assistant plugin bundle different copies of this package.
 */
export class InvestigationLensRequestError extends Error {
  constructor(
    public readonly code: InvestigationLensErrorCode,
    message: string
  ) {
    super(message);
    this.name = 'InvestigationLensRequestError';
  }
}

/** Converts any thrown value into a plain, typed lens error. */
export function toInvestigationLensError(err: unknown): InvestigationLensError {
  if (err && typeof err === 'object' && 'code' in err && isLensErrorCode(err.code)) {
    return { code: err.code, message: extractErrorMessage(err) };
  }
  return { code: 'llm_error', message: extractErrorMessage(err) };
}

// Extend window interface for global registry
declare global {
  interface Window {
    [key: string]: any;
  }
}

// Global registry keys for cross-plugin sharing: all plugins (and all copies
// of this package) must observe the same implementation, regardless of which
// bundle loads first.
const GLOBAL_IMPLEMENTATION_KEY = '__grafanaAssistantLensImplementation__';
const GLOBAL_INITIALIZER_KEY = '__grafanaAssistantLensInitializer__';
const GLOBAL_INITIALIZATION_PROMISE_KEY = '__grafanaAssistantLensInitPromise__';

export function setInvestigationLensImplementation(fn: InvestigationLensImplementation): void {
  const existing = window[GLOBAL_IMPLEMENTATION_KEY] as InvestigationLensImplementation | undefined;
  if (existing && existing !== fn) {
    console.warn('InvestigationLens implementation already set; ignoring subsequent call');
    return;
  }
  if (!existing) {
    window[GLOBAL_IMPLEMENTATION_KEY] = fn;
  }
}

/**
 * Register a lazy initializer that is called on first use. The Grafana
 * Assistant plugin registers this at bootstrap so the heavy implementation
 * loads only when a consumer actually requests a lens view.
 */
export function setInvestigationLensInitializer(fn: InvestigationLensInitializer): void {
  const existing = window[GLOBAL_INITIALIZER_KEY] as InvestigationLensInitializer | undefined;
  if (existing && existing !== fn) {
    console.warn('InvestigationLens initializer already set; ignoring subsequent call');
    return;
  }
  if (!existing) {
    window[GLOBAL_INITIALIZER_KEY] = fn;
  }
}

/**
 * Ensure the lens implementation is initialized, invoking the registered
 * initializer on first use. Throws an `unavailable` lens error when no
 * initializer is registered (assistant plugin missing or too old).
 */
export async function ensureInvestigationLensInitialized(): Promise<void> {
  const implementation = window[GLOBAL_IMPLEMENTATION_KEY] as InvestigationLensImplementation | undefined;
  if (implementation) {
    return;
  }

  const initializer = window[GLOBAL_INITIALIZER_KEY] as InvestigationLensInitializer | undefined;
  if (!initializer) {
    throw new InvestigationLensRequestError(
      'unavailable',
      'Investigation lens is not available. Make sure the Grafana Assistant plugin is installed and up to date.'
    );
  }

  // If initialization is in progress, wait for it
  const existingPromise = window[GLOBAL_INITIALIZATION_PROMISE_KEY] as Promise<void> | null | undefined;
  if (existingPromise) {
    return existingPromise;
  }

  const initPromise = (async () => {
    try {
      await initializer();
    } catch (err) {
      // Reset on error so retry is possible
      window[GLOBAL_INITIALIZATION_PROMISE_KEY] = null;
      throw err;
    }
  })();

  window[GLOBAL_INITIALIZATION_PROMISE_KEY] = initPromise;
  return initPromise;
}

export function getInvestigationLensImplementation(): InvestigationLensImplementation {
  const implementation = window[GLOBAL_IMPLEMENTATION_KEY] as InvestigationLensImplementation | undefined;
  if (!implementation) {
    throw new InvestigationLensRequestError(
      'unavailable',
      'Investigation lens is not available. Make sure the Grafana Assistant plugin is installed and up to date.'
    );
  }
  return implementation;
}
